import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import type {
  SavedGrant,
  WarrantGrantDraft,
  WarrantGrantEdit,
  WarrantGrantView,
  WarrantsResponse,
} from '@/api/warrants'
import { isQuantity, majorStringFromMinor, minorFromMajorString } from '@/domain/money'
import type { BuiltInWarrantTaxRegime, WarrantTaxRegime } from '@/domain/warrants'
import type { DeletedResponse, HoldingRow } from '~/api/client'
import { api } from '~/api/client'
import { WARRANT_TAX_KEY } from '~/state/warrant-tax'

/**
 * Both regimes named here rather than imported from the domain, which declares
 * them beside a Zod schema — a page importing that module drags the validator
 * into the browser build. The record is keyed by the domain's own union, so a
 * third regime added there fails the typecheck here rather than quietly
 * missing from the choice.
 */
export const REGIME_LABELS: Record<BuiltInWarrantTaxRegime, string> = {
  ligningsloven_28: 'Ligningsloven §28',
  ligningsloven_7p: 'Ligningsloven §7P',
}

/**
 * What choosing one does to the figures. §28 is taxed as personal income when
 * the warrants are exercised and §7P is not, so the two value the same grant
 * differently — which is why the form offers no default.
 */
export const REGIME_NOTES: Record<BuiltInWarrantTaxRegime, string> = {
  ligningsloven_28: 'The spread is taxed as personal income on exercise.',
  ligningsloven_7p: 'Nothing is taxed on exercise; it waits for the shares to be sold.',
}

export const REGIME_ORDER: readonly BuiltInWarrantTaxRegime[] = [
  'ligningsloven_28',
  'ligningsloven_7p',
]

/**
 * A regime this build has no label for is shown under its own key rather than
 * left out or left blank. The household named it on the rates card, so the key
 * is the only name anybody here has for it, and it is a better one than an
 * empty space where a name should be.
 */
export function regimeLabel(regime: string): string {
  return REGIME_LABELS[regime as BuiltInWarrantTaxRegime] ?? regime
}

/**
 * What choosing this one does to the figures, where the product knows. For a
 * regime the household added itself it says what is actually true — the rate is
 * whatever that household stated — rather than borrowing a sentence about a
 * Danish statute that does not apply to it.
 */
export function regimeNote(regime: string): string {
  return (
    REGIME_NOTES[regime as BuiltInWarrantTaxRegime] ??
    'Taxed on exercise at the rate you set for this regime on the warrant rates card.'
  )
}

export class GrantDraftError extends Error {}

/** Whether the grant converts into a company already entered, or a new one. */
export type GrantTarget = 'held' | 'new'

export interface VestingDraft {
  startDate: string
  cliffMonths: string
  durationMonths: string
  intervalMonths: string
}

export interface GrantDraft {
  target: GrantTarget
  /** The instrument id of the company chosen, as a select carries it. */
  holdingId: string
  companyName: string
  companyCurrency: string
  companySymbol: string
  entityKey: string
  grantedDate: string
  count: string
  strikeMajor: string
  currency: string
  /** Empty until it is chosen, because there is no regime to fall back on. */
  regime: WarrantTaxRegime | ''
  /**
   * Whether the regime is being named rather than picked. It decides which
   * refusal an empty box earns, and nothing else: what reaches the route is the
   * name either way, because a regime is only ever the name a rate is stored
   * under.
   */
  namingRegime: boolean
  expiryDate: string
  note: string
  /**
   * Whether a correction restates the vesting schedule. A grant's schedule is
   * not in what the report answers with, so an edit either replaces it whole or
   * leaves it alone — there is nothing to show as its current terms.
   */
  restateVesting: boolean
  vesting: VestingDraft
}

export function emptyGrantDraft(currency: string): GrantDraft {
  return {
    target: 'held',
    holdingId: '',
    companyName: '',
    companyCurrency: currency,
    companySymbol: '',
    entityKey: '',
    grantedDate: '',
    count: '',
    strikeMajor: '',
    currency,
    regime: '',
    namingRegime: false,
    expiryDate: '',
    note: '',
    restateVesting: true,
    vesting: { startDate: '', cliffMonths: '', durationMonths: '', intervalMonths: '' },
  }
}

/**
 * A grant on screen, as the boxes that would restate it. The schedule comes
 * back empty: the report answers with the dates vesting reaches rather than the
 * months it was entered as, and boxes filled by working backwards from those
 * dates would be this form's guess at terms nobody typed.
 */
export function draftFromGrant(grant: WarrantGrantView): GrantDraft {
  return {
    target: 'held',
    holdingId: String(grant.underlying.instrumentId),
    companyName: grant.underlying.name,
    companyCurrency: grant.underlying.currency,
    companySymbol: grant.underlying.symbol,
    entityKey: grant.entityKey,
    grantedDate: grant.grantedDate,
    count: grant.count,
    strikeMajor: majorStringFromMinor(grant.strikeMinor),
    currency: grant.currency,
    regime: grant.regime,
    // Picked, not named: the rates route offers every regime this household's
    // own grants name, so the one this grant was filed under is in the menu.
    namingRegime: false,
    expiryDate: grant.expiryDate ?? '',
    note: grant.note ?? '',
    restateVesting: false,
    vesting: { startDate: '', cliffMonths: '', durationMonths: '', intervalMonths: '' },
  }
}

export interface UnderlyingChoice {
  instrumentId: number
  symbol: string
  name: string
}

/**
 * Every company a grant can be filed against: the ones a position names, and
 * the ones only another grant does.
 *
 * A grant creates the company it converts into without writing a position —
 * warrants are not shares — so the holdings are not the whole list. Left to
 * them, a second grant over the same company would have to name it again, and
 * naming it again creates a second company with a valuation on neither.
 */
export function underlyingChoices(
  rows: readonly HoldingRow[],
  grants: readonly WarrantGrantView[],
): UnderlyingChoice[] {
  const found = new Map<number, UnderlyingChoice>()
  for (const row of rows) {
    found.set(row.instrumentId, {
      instrumentId: row.instrumentId,
      symbol: row.symbol,
      name: row.instrumentName,
    })
  }
  for (const grant of grants) {
    found.set(grant.underlying.instrumentId, {
      instrumentId: grant.underlying.instrumentId,
      symbol: grant.underlying.symbol,
      name: grant.underlying.name,
    })
  }
  return [...found.values()].sort((a, b) => a.symbol.localeCompare(b.symbol))
}

function wholeMonths(value: string): number | undefined {
  const trimmed = value.trim()
  return /^\d+$/.test(trimmed) ? Number(trimmed) : undefined
}

function sendsVesting(draft: GrantDraft, editing: boolean): boolean {
  return !editing || draft.restateVesting
}

/**
 * Why this grant cannot be saved yet, in the words of whoever would refuse it.
 *
 * Two of these are not the route's rules but the valuation's: it takes a count
 * below zero and a cliff longer than the schedule, and the report then cannot
 * value the grant at all — including the row that would let it be corrected.
 */
export function refuseGrant(draft: GrantDraft, editing = false): string | undefined {
  if (!editing) {
    if (draft.target === 'held' && draft.holdingId === '') {
      return 'choose the company these warrants convert into'
    }
    if (draft.target === 'new') {
      if (draft.companyName.trim() === '') return 'a company name is required'
      if (draft.companyCurrency.trim().length !== 3) {
        return 'the company needs a three-letter currency'
      }
    }
  }

  if (draft.entityKey === '') return 'an entity is required'
  if (draft.grantedDate === '') return 'the date the warrants were granted is required'

  const count = draft.count.trim()
  if (!isQuantity(count)) return 'a count is required, as a decimal number'
  if (count.startsWith('-')) return 'a count cannot be negative'

  if (draft.strikeMajor.trim() === '') return 'a strike per warrant is required'
  try {
    if (minorFromMajorString(draft.strikeMajor.trim()) < 0) return 'a strike cannot be negative'
  } catch (error) {
    return `strike: ${(error as Error).message}`
  }

  if (draft.currency.trim().length !== 3) return 'a three-letter currency is required'
  if (draft.namingRegime) {
    if (draft.regime.trim() === '') return 'name the regime the grant was written under'
  } else if (draft.regime === '') {
    return 'choose which regime the grant was written under — there is no default'
  }

  if (!sendsVesting(draft, editing)) return undefined

  const { startDate } = draft.vesting
  const duration = wholeMonths(draft.vesting.durationMonths)
  const interval = wholeMonths(draft.vesting.intervalMonths)
  const cliff = wholeMonths(draft.vesting.cliffMonths)

  if (startDate === '') return 'the date vesting starts is required'
  if (duration === undefined || duration === 0) {
    return 'vesting runs over a whole number of months, and at least one'
  }
  if (interval === undefined || interval === 0) {
    return 'warrants vest every whole number of months, and at least one'
  }
  if (cliff === undefined) return 'a cliff is a whole number of months, or none'
  if (cliff > duration) return `a cliff of ${cliff} months does not fit in ${duration}`

  return undefined
}

/** Trimmed, because a name is stored as typed and read back as a key. */
function chosenRegime(draft: GrantDraft): WarrantTaxRegime {
  const regime = draft.regime.trim()
  if (regime === '') throw new GrantDraftError('no regime was chosen')
  return regime
}

/**
 * All four every time, taken from the edit's schema rather than the entry's:
 * the interval has a default there, and a schedule sent without one would be
 * restated as monthly vesting nobody asked for.
 */
function toVesting(draft: VestingDraft): NonNullable<WarrantGrantEdit['vesting']> {
  return {
    startDate: draft.startDate,
    cliffMonths: Number(draft.cliffMonths.trim()),
    durationMonths: Number(draft.durationMonths.trim()),
    intervalMonths: Number(draft.intervalMonths.trim()),
  }
}

/** Blank is the term nobody wrote down, which the report says out loud. */
function orNull(value: string): string | null {
  return value.trim() === '' ? null : value.trim()
}

export function toGrantBody(draft: GrantDraft): WarrantGrantDraft {
  return {
    entityKey: draft.entityKey,
    ...(draft.target === 'held'
      ? { holdingId: Number(draft.holdingId) }
      : {
          underlying: {
            name: draft.companyName.trim(),
            currency: draft.companyCurrency.trim().toUpperCase(),
            ...(draft.companySymbol.trim() === ''
              ? {}
              : { symbol: draft.companySymbol.trim().toUpperCase() }),
          },
        }),
    grantedDate: draft.grantedDate,
    count: draft.count.trim(),
    strikeMinor: minorFromMajorString(draft.strikeMajor.trim()),
    currency: draft.currency.trim().toUpperCase(),
    expiryDate: orNull(draft.expiryDate),
    vesting: toVesting(draft.vesting),
    regime: chosenRegime(draft),
    note: orNull(draft.note),
  }
}

/**
 * The terms an edit restates. What the grant is over is left out because the
 * route leaves it out: a grant pointed at another company is a different grant
 * rather than a corrected one.
 */
export function toGrantEdit(draft: GrantDraft): WarrantGrantEdit {
  return {
    entityKey: draft.entityKey,
    grantedDate: draft.grantedDate,
    count: draft.count.trim(),
    strikeMinor: minorFromMajorString(draft.strikeMajor.trim()),
    currency: draft.currency.trim().toUpperCase(),
    expiryDate: orNull(draft.expiryDate),
    regime: chosenRegime(draft),
    note: orNull(draft.note),
    ...(draft.restateVesting ? { vesting: toVesting(draft.vesting) } : {}),
  }
}

const WARRANTS_KEY = ['warrants'] as const

/**
 * `enabled` defaults open for the page that reports on grants, but the
 * navigation asks the same question before it knows whether anybody is signed
 * in — it passes `false` until a household is, so a visitor sees no warrants
 * request before the session does.
 */
export function useWarrants(enabled = true): UseQueryResult<WarrantsResponse> {
  return useQuery({ queryKey: WARRANTS_KEY, queryFn: () => api.warrants(), enabled })
}

/**
 * The grants, and the rates read beside them. A grant names the regime it was
 * written under, so the boxes the rates card offers follow what is filed — a
 * household that has just named one would otherwise find no box for it until
 * something else happened to refetch.
 */
function useInvalidateGrants(): () => Promise<void> {
  const client = useQueryClient()
  return async () => {
    await client.invalidateQueries({ queryKey: WARRANTS_KEY })
    await client.invalidateQueries({ queryKey: WARRANT_TAX_KEY })
  }
}

export function useSaveWarrantGrant(): UseMutationResult<SavedGrant, Error, WarrantGrantDraft> {
  const invalidate = useInvalidateGrants()
  return useMutation({ mutationFn: api.saveWarrantGrant, onSuccess: invalidate })
}

export function useEditWarrantGrant(): UseMutationResult<
  SavedGrant,
  Error,
  { id: number; edit: WarrantGrantEdit }
> {
  const invalidate = useInvalidateGrants()
  return useMutation({
    mutationFn: ({ id, edit }: { id: number; edit: WarrantGrantEdit }) =>
      api.editWarrantGrant(id, edit),
    onSuccess: invalidate,
  })
}

export function useRemoveWarrantGrant(): UseMutationResult<DeletedResponse, Error, number> {
  const invalidate = useInvalidateGrants()
  return useMutation({ mutationFn: api.deleteWarrantGrant, onSuccess: invalidate })
}
